import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  calendarDaysUntilInTZ,
  formatCurrency,
  parseBarcode,
} from '#common/utils';
import EnhancedTable, { EnhancedTableData, EnhancedTableHeader } from '#components/EnhancedTable';
import OrderItemDetails, { OrderItemDetailsItem, OrderItemDetailsOrder } from '#components/OrderItemDetails';

interface OrderItemRow {
  id?: string | number;
  fromInventory?: boolean;
  barcodeItems?: Array<{ barcode: string }>;
  barcodes?: string[];
  amounts?: number;
  unitPrice?: number;
  totalPrice?: number;
  confirmDeadline?: string | number | Date | null;
  [key: string]: unknown;
}

interface OrderShape {
  invoices: { items: Array<{ value: string; [key: string]: unknown }> };
  barcodeItems: Array<{ barcode: string; itemId?: string | number; received?: boolean }>;
  packages: Array<{ barcodeItems: string[] }>;
  [key: string]: unknown;
}

interface UserOrderItemsProps {
  orderItems: EnhancedTableData;
  order: OrderShape;
  // rows are cancelled items (or the whole order is cancelled)
  cancelled?: boolean;
  // rows wait for a ship date; show the confirmation countdown
  pending?: boolean;
  isInvoice?: boolean;
}

function getQTYForInvoice(order: OrderShape, item: OrderItemRow) {
  return order.invoices.items.filter((itm) => {
    const parsed = parseBarcode(itm.value);
    if (!parsed) {
      return false;
    }

    const { fromInventory } = item;
    if (fromInventory) {
      const itemBarcodes = item.barcodeItems && item.barcodeItems.length
        ? item.barcodeItems.map((bi) => bi.barcode) : [];
      return itemBarcodes.some((b) => {
        const p = parseBarcode(b);
        return (parsed.itemId === p?.itemId);
      });
    }

    return (parsed.itemId === item.id);
  }).length;
}

export default function UserOrderItems(props: UserOrderItemsProps) {
  const {
    orderItems,
    order,
    cancelled,
    pending,
    isInvoice,
  } = props;
  const { t } = useTranslation('order');
  const timeZone = useSelector((s: { appConfig: { timeZone?: string } }) => s.appConfig.timeZone);

  if (!orderItems.rows.length) {
    return null;
  }

  const tableHeaders: EnhancedTableHeader[] = [{
    id: '',
    label: t('items.item'),
    renderer: (item: OrderItemRow) => (
      <OrderItemDetails
        item={item as unknown as OrderItemDetailsItem}
        order={order as unknown as OrderItemDetailsOrder}
        cancelled={!!cancelled}
      />
    ),
  }, {
    id: 'barcodes',
    label: t('items.barcodes'),
    renderer: (item: OrderItemRow) => {
      let barcodes: string[];
      if (isInvoice && item.fromInventory) {
        const itemBarcodes = item.barcodeItems?.map((bi) => bi.barcode) || [];
        barcodes = order.invoices.items
          .filter((itm) => itemBarcodes.includes(itm.value))
          .map((itm) => itm.value);
      } else if (isInvoice) {
        barcodes = order.invoices.items
          .filter((itm) => {
            const parsed = parseBarcode(itm.value);
            return parsed && parsed.itemId === item.id;
          })
          .map((itm) => itm.value);
      } else if (item.fromInventory) {
        barcodes = item.barcodeItems && item.barcodeItems.length
          ? item.barcodeItems.map((bi) => bi.barcode) : [];
      } else {
        barcodes = order.barcodeItems
          .filter((bi) => bi.itemId === item.id)
          .map((bi) => bi.barcode);
      }
      return barcodes.map((bc, idx) => (<div key={idx}>{bc}</div>));
    },
  }, {
    id: 'amounts',
    label: t('items.qty'),
    renderer: (item: OrderItemRow) => {
      if (cancelled) {
        return 0;
      }

      if (isInvoice) {
        return getQTYForInvoice(order, item);
      }

      return item.amounts;
    },
  }, {
    id: 'unitPrice',
    label: t('items.unitPrice'),
    renderer: (item: OrderItemRow) => (cancelled
      ? `${formatCurrency(0)}`
      : `${formatCurrency(item.unitPrice as number)}`),
  }, {
    id: 'totalPrice',
    label: t('items.total'),
    renderer: (item: OrderItemRow) => {
      if (cancelled) {
        return formatCurrency(0);
      }

      if (isInvoice) {
        const qty = getQTYForInvoice(order, item);
        const total = (item.unitPrice as number) * qty;
        return formatCurrency(total);
      }

      return formatCurrency(item.totalPrice as number);
    },
  }];

  if (!isInvoice) {
    tableHeaders.push({
      id: 'status',
      label: t('items.status'),
      renderer: (item: OrderItemRow) => {
        if (cancelled) {
          return t('status.canceled');
        }

        if (pending && item.confirmDeadline) {
          const daysLeft = calendarDaysUntilInTZ(timeZone, item.confirmDeadline);
          return daysLeft > 0
            ? t('items.confirmCountdown', { count: daysLeft })
            : t('items.confirmDueToday');
        }

        const barcodes = order.barcodeItems.filter((bi) => {
          if (bi.itemId === item.id) {
            // normal order items
            return true;
          }

          if (item.barcodes && item.barcodes.includes(bi.barcode)) {
            // inventory items
            return true;
          }

          return false;
        });
        if (!barcodes.length) {
          return <div>{t('items.orderConfirmed')}</div>;
        }
        const received = barcodes.filter((b) => b.received);
        const shipped = received.filter((rc) => {
          return order.packages.some((p) => p.barcodeItems.includes(rc.barcode));
        });
        return (
          <>
            {/* <div>
              {`In Warehouse (${received.length} of ${barcodes.length})`}
            </div> */}
            <div>
              {t('items.shipped', { shipped: shipped.length, total: barcodes.length })}
            </div>
          </>
        );
      },
    });
  }

  return (
    <Box sx={{ mb: 5 }}>
      <EnhancedTable
        name="order-detail-customer"
        headers={tableHeaders}
        {...orderItems}
      />
    </Box>
  );
}
